import TR from 'utils/TR';
import { Chart } from 'chart.js';
import useRect from 'hooks/common/useRect';
import React from 'react';
import T from 'utils/T';
import ex from 'utils/ex';
import Recoil from 'recoil';
import * as AppStore from 'stores/AppStore';

import 'chartjs-adapter-luxon';
import useSetChartData from 'hooks/app/useSetChartData';

const DEFAULT_VALUE = NaN;
const columns = Array.from({ length: 25 }).map((name, index) => `${index.toString().padStart(2, '0')}00`);
const config = (hash, onComplete, duration) => ({
  type: 'line',
  data: {
    labels: columns,
    datasets: [
      {
        data: columns.map((name, index) => {
          return hash[name]?.bld_sugr_5per || DEFAULT_VALUE;
        }),
        pointRadius: 0,
        borderWidth: 0,
        cubicInterpolationMode: 'monotone',
        tension: 0.4,
      }, {
        data: columns.map((name, index) => {
          return hash[name]?.bld_sugr_95per || DEFAULT_VALUE;
        }),
        pointRadius: 0,
        borderWidth: 0,
        fill: '-1',
        cubicInterpolationMode: 'monotone',
        backgroundColor: '#347AFF20',
        tension: 0.4,
      }, {
        data: columns.map((name, index) => {
          return hash[name]?.bld_sugr_25per || DEFAULT_VALUE;
        }),
        pointRadius: 0,
        borderWidth: 0,
        cubicInterpolationMode: 'monotone',
        tension: 0.4,
      }, {
        data: columns.map((name, index) => {
          return hash[name]?.bld_sugr_75per || DEFAULT_VALUE;
        }),
        pointRadius: 0,
        borderWidth: 0,
        fill: '-1',
        cubicInterpolationMode: 'monotone',
        backgroundColor: '#347AFF20',
        tension: 0.4,
      }, {
        data: columns.map((name, index) => {
          return hash[name]?.bld_sugr_50per || DEFAULT_VALUE;
        }),
        pointRadius: 0,
        borderWidth: 3,
        cubicInterpolationMode: 'monotone',
        borderColor: '#2251A9',
        tension: 0.4,
      }
    ]
  },
  options: {
    devicePixelRatio: 4,
    animation: {
      duration,
      onComplete,
    },
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
    },
    scales: {
      x: {
        display: true,
        ticks: {
          callback: (value) => {
            if (value % 6 === 0) {
              return value + 'H';
            } else {
              return '';
            }
          },
          stepSize: 6,
          font: {
            family: 'Pretendard',
            size: 11,
            weight: 400,
            color: '#AAAAAA',
          },
        },
        title: {
          display: true
        },
        grid: {
          drawTicks: false,
        },
        afterFit: (scale) => {
          scale.height = 13;
        }
      },
      y: {
        type: 'logarithmic',
        display: true,
        afterTickToLabelConversion: (scaleInstance) => {
          const ticks = scaleInstance.ticks;
          const newTicks = ticks?.filter((tick) => {
            switch (tick.value) {
              case 0: return true;
              case 54: return true;
              case 70: return true;
              case 180: return true;
              case 250: return true;
              case 350: return true;
              default: return false;
            }
          });
          scaleInstance.ticks = newTicks;
        },
        title: {
          display: true,
        },
        min: 40,
        max: 600,
        ticks: {
          callback: (value) => {
            switch (value) {
              case 0:
              case 54:
              case 70:
              case 180:
              case 250:
              case 350:
                return value;
              default:
                return '';
            }
          },
          font: {
            family: 'Pretendard',
            size: 11,
            weight: 400,
            color: '#AAAAAA',
          },
          // stepSize: 1,
          // display: false,
        },
        afterBuildTicks: (chart) => {
          chart.ticks = [];
          chart.ticks.push({ value: 54 });
          chart.ticks.push({ value: 70 });
          chart.ticks.push({ value: 180 });
          chart.ticks.push({ value: 250 });
          chart.ticks.push({ value: 350 });
        },
        grid: {
          drawTicks: false,
        },
      }
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
      tooltip: {
        enabled: true,
        callbacks: {
          title: function (context) {
            const label = (context[0].label || '').split('');
            label.splice(2, 0, ':');
            return label.join('');
          }
        },
      },
      // annotation: {
      //   common: {
      //     drawTime: 'afterDraw'
      //   },
      //   annotations: {
      //     annotation1: {
      //       type: 'point',
      //       backgroundColor: 'rgba(0, 150, 0, 0.2)',
      //       borderColor: 'rgba(0, 150, 0)',
      //       borderRadius: 4,
      //       borderWidth: 1,
      //       init: () => ({ centerY: 0 }),
      //       radius: 40,
      //       xMax: 2.5,
      //       xMin: 0.5,
      //       yMax: 30,
      //       yMin: 10
      //     },
      //   }
      // }
    },
  },
});

export default function GlocoseProfileChart(props) {
  const {
    duration,
    ...rest
  } = props;

  const agp = Recoil.useRecoilValue(AppStore.assets.agp);

  const ref = React.useRef();
  const rect = useRect(ref);
  const canvasRef = React.useRef();
  const chartRef = React.useRef();

  const setChartData = useSetChartData('GlocoseProfileChart');
  const chartDataTimeoutRef = React.useRef();
  const onComplete = React.useCallback((animation) => {
    if (agp.uninitialized) return;
    if (duration) return;
    if (!animation.chart.canvas.width) return;
    if (!animation.chart.canvas.height) return;
    clearTimeout(chartDataTimeoutRef.current);
    const url = animation.chart.canvas.toDataURL('image/png');
    const width = animation.chart.canvas.width;
    const height = animation.chart.canvas.height;
    chartDataTimeoutRef.current = setTimeout(() => {
      setChartData && setChartData(() => ({ url, width, height }));
      URL.revokeObjectURL(url);
    }, 6000);
  }, [agp.uninitialized, duration, setChartData]);

  const hash = React.useMemo(() => (agp.list || []).reduce((a, c) => ({
    ...a,
    [c.time]: c,
  }), {}), [agp]);

  React.useEffect(() => {
    const chart = new Chart(canvasRef.current, config([], onComplete, duration));
    chartRef.current = chart;
    return () => {
      chart.destroy();
      delete chart.config;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const configuration = config(hash, onComplete, duration);
    configuration.data.datasets.forEach((dataset, datasetIndex) => {
      dataset.data.forEach((datum, datumIndex) => {
        chartRef.current.config.data.datasets[datasetIndex].data[datumIndex] = datum;
      });
    });
    chartRef.current.update();
  }, [duration, hash, onComplete]);

  React.useLayoutEffect(() => {
    if (!chartRef.current) return;
    const frameId = requestAnimationFrame(() => {
      chartRef.current.update();
    });
    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [rect]);
  return (
    <div
      {...ex(['Chart', 'GlocoseProfileChart'], rest)}
      {...{ ref }}
    >
      <canvas
        {...{
          ref: canvasRef,
          width: rect.width,
          height: rect.height,
        }}
      />
    </div>
  );
}

